import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  LocalShipping as LocalShippingIcon,
  CreditCard as CreditCardIcon,
  VerifiedUser as VerifiedUserIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Login as LoginIcon,
  Sell as SellIcon,
  ShoppingBag as ShoppingBagIcon,
  SupportAgent as SupportAgentIcon,
  Article as ArticleIcon,
  HelpOutline as HelpOutlineIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

type SupportCategoryId =
  | 'start'
  | 'first-steps'
  | 'selling'
  | 'buying'
  | 'shipping'
  | 'payments'
  | 'trust'
  | 'profile'
  | 'community'
  | 'login';

type Topic = {
  id: SupportCategoryId;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
};

type Article = {
  id: string;
  categoryId: SupportCategoryId;
  title: string;
  description: string;
};

type Faq = {
  id: string;
  categoryId: SupportCategoryId;
  q: string;
  a: string;
};

const topics: Topic[] = [
  {
    id: 'first-steps',
    title: 'Erste Schritte',
    subtitle: 'Konto, Orientierung, erste Bestellung',
    icon: <HelpOutlineIcon />,
  },
  {
    id: 'selling',
    title: 'Verkaufen',
    subtitle: 'Inserate, Preise, Reichweite',
    icon: <SellIcon />,
  },
  {
    id: 'buying',
    title: 'Kaufen',
    subtitle: 'Bestellen, Favoriten, Warenkorb',
    icon: <ShoppingBagIcon />,
  },
  {
    id: 'shipping',
    title: 'Versand',
    subtitle: 'Lieferung, Tracking, Rückgabe',
    icon: <LocalShippingIcon />,
  },
  {
    id: 'payments',
    title: 'Zahlungen & Auszahlung',
    subtitle: 'Zahlarten, Rechnungen, Auszahlungen',
    icon: <CreditCardIcon />,
  },
  {
    id: 'trust',
    title: 'Vertrauen & Sicherheit',
    subtitle: 'Schutz, Meldungen, Betrugsprävention',
    icon: <VerifiedUserIcon />,
  },
  {
    id: 'profile',
    title: 'Mein Profil & Einstellungen',
    subtitle: 'Daten, Adressen, Benachrichtigungen',
    icon: <PersonIcon />,
  },
  {
    id: 'community',
    title: 'Community',
    subtitle: 'Regeln, Feedback, Umgang',
    icon: <GroupIcon />,
  },
  {
    id: 'login',
    title: 'Registrierung & Login',
    subtitle: 'Anmeldung, Passwort, Verifizierung',
    icon: <LoginIcon />,
  },
];

const sidebarItems: { id: SupportCategoryId; label: string }[] = [
  { id: 'start', label: 'Start' },
  { id: 'first-steps', label: 'Erste Schritte' },
  { id: 'selling', label: 'Verkaufen' },
  { id: 'buying', label: 'Kaufen' },
  { id: 'shipping', label: 'Versand' },
  { id: 'payments', label: 'Zahlungsmethoden/Auszahlung' },
  { id: 'trust', label: 'Vertrauen und Sicherheit' },
  { id: 'profile', label: 'Mein Profil & Einstellungen' },
  { id: 'community', label: 'Community' },
  { id: 'login', label: 'Registrierung & Login' },
];

const articles: Article[] = [
  {
    id: 'a-1',
    categoryId: 'buying',
    title: 'Bestellung verfolgen',
    description: 'So findest du Status, Tracking und Zustellinfos zu deiner Bestellung.',
  },
  {
    id: 'a-2',
    categoryId: 'shipping',
    title: 'Rückgabe & Erstattung',
    description: 'Schritt-für-Schritt: Rückgabe anstoßen und Erstattung erhalten.',
  },
  {
    id: 'a-3',
    categoryId: 'payments',
    title: 'Zahlungsmethoden ändern',
    description: 'Verwalte Karten, Zahlungsarten und Rechnungsinfos sicher.',
  },
  {
    id: 'a-4',
    categoryId: 'selling',
    title: 'Inserat optimieren',
    description: 'Tipps für bessere Fotos, Titel und mehr Sichtbarkeit.',
  },
  {
    id: 'a-5',
    categoryId: 'trust',
    title: 'Sicher einkaufen & verkaufen',
    description: 'Erkenne verdächtige Anfragen und halte dich an Best Practices.',
  },
];

const faqs: Faq[] = [
  {
    id: 'f-1',
    categoryId: 'start',
    q: 'Wie können wir dir helfen?',
    a: 'Nutze die Suche oder wähle ein Thema. Du findest hier Artikel, häufige Fragen und schnelle Hilfen.',
  },
  {
    id: 'f-2',
    categoryId: 'buying',
    q: 'Kann ich eine Bestellung stornieren?',
    a: 'Wenn die Bestellung noch nicht versendet wurde, ist eine Stornierung häufig möglich. Prüfe den Status in deinen Bestellungen.',
  },
  {
    id: 'f-3',
    categoryId: 'shipping',
    q: 'Was mache ich, wenn mein Paket nicht ankommt?',
    a: 'Überprüfe den Tracking-Status. Falls sich nichts bewegt, kontaktiere den Verkäufer oder unseren Support mit Bestellnummer.',
  },
  {
    id: 'f-4',
    categoryId: 'payments',
    q: 'Welche Zahlungsmethoden werden unterstützt?',
    a: 'Je nach Plattform-Einstellungen stehen z.B. Karte, PayPal oder Überweisung zur Verfügung.',
  },
  {
    id: 'f-5',
    categoryId: 'trust',
    q: 'Wie melde ich ein Problem oder einen Nutzer?',
    a: 'Nutze die Melden-Funktion auf der jeweiligen Seite. Wir prüfen den Fall und melden uns, falls weitere Infos nötig sind.',
  },
];

const categoryTitle: Record<SupportCategoryId, string> = {
  start: 'Start',
  'first-steps': 'Erste Schritte',
  selling: 'Verkaufen',
  buying: 'Kaufen',
  shipping: 'Versand',
  payments: 'Zahlungsmethoden/Auszahlung',
  trust: 'Vertrauen und Sicherheit',
  profile: 'Mein Profil & Einstellungen',
  community: 'Community',
  login: 'Registrierung & Login',
};

const KundencenterPage: React.FC = () => {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const [activeCategory, setActiveCategory] = React.useState<SupportCategoryId>('start');
  const [query, setQuery] = React.useState('');

  const fromState = (location.state as any)?.from as string | undefined;

  React.useEffect(() => {
    if (!fromState) return;
    try {
      sessionStorage.setItem('kundencenter_last_from', fromState);
    } catch {
      // ignore
    }
  }, [fromState]);

  const handleBack = React.useCallback(() => {
    const safeNav = (to: string) => {
      navigate(to);
    };

    if (fromState) {
      safeNav(fromState);
      return;
    }

    try {
      const saved = sessionStorage.getItem('kundencenter_last_from');
      if (saved) {
        safeNav(saved);
        return;
      }
    } catch {
      // ignore
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    safeNav('/dashboard');
  }, [fromState, navigate]);

  const normalizedQuery = query.trim().toLowerCase();

  const filteredTopics = React.useMemo(() => {
    if (!normalizedQuery) return topics;
    return topics.filter((t) => {
      const hay = `${t.title} ${t.subtitle}`.toLowerCase();
      return hay.includes(normalizedQuery);
    });
  }, [normalizedQuery]);

  const filteredArticles = React.useMemo(() => {
    const pool = activeCategory === 'start' ? articles : articles.filter((a) => a.categoryId === activeCategory);
    if (!normalizedQuery) return pool;
    return pool.filter((a) => {
      const hay = `${a.title} ${a.description}`.toLowerCase();
      return hay.includes(normalizedQuery);
    });
  }, [activeCategory, normalizedQuery]);

  const filteredFaqs = React.useMemo(() => {
    const pool = activeCategory === 'start' ? faqs : faqs.filter((f) => f.categoryId === activeCategory);
    if (!normalizedQuery) return pool;
    return pool.filter((f) => {
      const hay = `${f.q} ${f.a}`.toLowerCase();
      return hay.includes(normalizedQuery);
    });
  }, [activeCategory, normalizedQuery]);

  const headerSubtitle = React.useMemo(() => {
    if (activeCategory === 'start') return 'Ich habe eine allgemeine Frage';
    return categoryTitle[activeCategory];
  }, [activeCategory]);

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 2.5, md: 4 } }}>
        <Box
          sx={(t: Theme) => ({
            mb: { xs: 2, md: 3 },
            p: { xs: 2, md: 2.5 },
            borderRadius: 4,
            border: `1px solid ${t.palette.divider}`,
            backgroundColor: t.palette.background.paper,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          })}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'nowrap' }}>
              <Button
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                sx={(t: Theme) => ({
                  borderRadius: 999,
                  px: 1.5,
                  py: 0.85,
                  fontWeight: 900,
                  textTransform: 'none',
                  border: `1px solid ${t.palette.divider}`,
                  color: t.palette.text.primary,
                  backgroundColor: t.palette.background.paper,
                  '&:hover': {
                    backgroundColor: t.palette.action.hover,
                  },
                })}
              >
                Zurück
              </Button>
              <Typography variant={isMdDown ? 'h5' : 'h4'} sx={{ fontWeight: 950, lineHeight: 1.1 }}>
                Kundencenter
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Wie können wir dir helfen?
            </Typography>
          </Box>

          <Chip
            icon={<SupportAgentIcon />}
            label="Support & Hilfe"
            sx={(t: Theme) => ({
              fontWeight: 900,
              borderRadius: 999,
              px: 0.75,
              backgroundColor: t.palette.action.hover,
              border: `1px solid ${t.palette.divider}`,
              alignSelf: { xs: 'flex-start', sm: 'center' },
            })}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
          {!isMdDown && (
            <Card
              sx={(t: Theme) => ({
                width: { xs: '100%', md: 280 },
                position: { md: 'sticky' },
                top: { md: 18 },
                border: `1px solid ${t.palette.divider}`,
                boxShadow: 'none',
                overflow: 'hidden',
              })}
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ fontWeight: 950 }}>Hilfe-Center</Typography>
                </Box>
                <Divider />
                <Stack spacing={0}>
                  {sidebarItems.map((it) => {
                    const active = it.id === activeCategory;
                    return (
                      <Box
                        key={it.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setActiveCategory(it.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setActiveCategory(it.id);
                          }
                        }}
                        sx={(t: Theme) => ({
                          px: 2,
                          py: 1.35,
                          cursor: 'pointer',
                          backgroundColor: active ? t.palette.action.hover : 'transparent',
                          borderLeft: active ? `3px solid ${t.palette.primary.main}` : '3px solid transparent',
                          '&:hover': {
                            backgroundColor: t.palette.action.hover,
                          },
                        })}
                      >
                        <Typography sx={{ fontWeight: active ? 900 : 700, color: 'text.primary' }}>{it.label}</Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isMdDown && (
              <Card
                sx={(t: Theme) => ({
                  border: `1px solid ${t.palette.divider}`,
                  boxShadow: 'none',
                  overflow: 'hidden',
                  mb: 2,
                })}
              >
                <CardContent sx={{ p: 2 }}>
                  <TextField
                    select
                    fullWidth
                    label="Thema"
                    value={activeCategory}
                    onChange={(e) => setActiveCategory(e.target.value as SupportCategoryId)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 999 } }}
                  >
                    {sidebarItems.map((it) => (
                      <MenuItem key={it.id} value={it.id}>
                        {it.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </CardContent>
              </Card>
            )}

            <Card
              sx={(t: Theme) => ({
                border: `1px solid ${t.palette.divider}`,
                boxShadow: 'none',
                overflow: 'hidden',
              })}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography sx={{ fontWeight: 950, mb: 0.5 }}>{headerSubtitle}</Typography>
                <TextField
                  fullWidth
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Gib deine Frage ein …"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mt: 1,
                    '& .MuiOutlinedInput-root': { borderRadius: 999 },
                  }}
                />
              </CardContent>
            </Card>

            <Box sx={{ mt: 2.25 }}>
              <Typography sx={{ fontWeight: 950, mb: 1.25 }}>Allgemeine Themen</Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                    md: '1fr 1fr 1fr',
                  },
                  gap: 1.5,
                }}
              >
                {filteredTopics.map((t) => (
                  <Card
                    key={t.id}
                    sx={(th: Theme) => ({
                      border: `1px solid ${th.palette.divider}`,
                      boxShadow: 'none',
                      overflow: 'hidden',
                    })}
                  >
                    <CardActionArea
                      onClick={() => setActiveCategory(t.id)}
                      sx={(th: Theme) => ({
                        p: 2,
                        height: '100%',
                        '&:hover .kc-icon': {
                          backgroundColor: th.palette.primary.main + '14',
                          borderColor: th.palette.primary.main + '2A',
                        },
                      })}
                    >
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Box
                          className="kc-icon"
                          sx={(th: Theme) => ({
                            width: 44,
                            height: 44,
                            borderRadius: 3,
                            display: 'grid',
                            placeItems: 'center',
                            color: th.palette.primary.main,
                            backgroundColor: th.palette.action.hover,
                            border: `1px solid ${th.palette.divider}`,
                            transition: 'all 0.15s ease',
                            flex: '0 0 auto',
                          })}
                        >
                          {t.icon}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 950, lineHeight: 1.2 }}>{t.title}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }} noWrap>
                            {t.subtitle}
                          </Typography>
                        </Box>
                      </Box>
                    </CardActionArea>
                  </Card>
                ))}
              </Box>
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ fontWeight: 950, mb: 1.25 }}>Empfohlene Artikel</Typography>

              <Stack spacing={1.25}>
                {filteredArticles.length === 0 ? (
                  <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                    <CardContent sx={{ p: 2.25 }}>
                      <Typography sx={{ fontWeight: 900 }}>Keine Treffer</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Versuche einen anderen Suchbegriff oder wähle ein anderes Thema.
                      </Typography>
                    </CardContent>
                  </Card>
                ) : (
                  filteredArticles.map((a) => (
                    <Card
                      key={a.id}
                      sx={(t: Theme) => ({
                        border: `1px solid ${t.palette.divider}`,
                        boxShadow: 'none',
                        overflow: 'hidden',
                      })}
                    >
                      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', p: 2 }}>
                        <Box
                          sx={(t: Theme) => ({
                            width: 40,
                            height: 40,
                            borderRadius: 3,
                            display: 'grid',
                            placeItems: 'center',
                            color: t.palette.primary.main,
                            backgroundColor: t.palette.primary.main + '14',
                            border: `1px solid ${t.palette.primary.main}2A`,
                            flex: '0 0 auto',
                          })}
                        >
                          <ArticleIcon fontSize="small" />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 950, lineHeight: 1.2 }}>{a.title}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                            {a.description}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          sx={{ borderRadius: 999, fontWeight: 900, px: 2, whiteSpace: 'nowrap' }}
                        >
                          Öffnen
                        </Button>
                      </Box>
                    </Card>
                  ))
                )}
              </Stack>
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ fontWeight: 950, mb: 1.25 }}>Häufige Fragen</Typography>

              <Card sx={(t: Theme) => ({ border: `1px solid ${t.palette.divider}`, boxShadow: 'none' })}>
                <CardContent sx={{ p: { xs: 0, sm: 0 } }}>
                  {filteredFaqs.map((f, idx) => (
                    <Box key={f.id}>
                      <Accordion
                        disableGutters
                        elevation={0}
                        sx={(t: Theme) => ({
                          borderTop: idx === 0 ? '0' : `1px solid ${t.palette.divider}`,
                          '&:before': { display: 'none' },
                        })}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 0.5 }}>
                          <Typography sx={{ fontWeight: 900 }}>{f.q}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                            {f.a}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <Card
                sx={(t: Theme) => ({
                  border: `1px solid ${t.palette.divider}`,
                  boxShadow: 'none',
                  overflow: 'hidden',
                })}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                      <Box
                        sx={(t: Theme) => ({
                          width: 44,
                          height: 44,
                          borderRadius: 3,
                          display: 'grid',
                          placeItems: 'center',
                          color: t.palette.primary.main,
                          backgroundColor: t.palette.action.hover,
                          border: `1px solid ${t.palette.divider}`,
                        })}
                      >
                        <SupportAgentIcon />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 950, lineHeight: 1.2 }}>Noch Fragen?</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                          Kontaktiere unser Support-Team oder starte eine Anfrage.
                        </Typography>
                      </Box>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button
                        variant="contained"
                        startIcon={<SupportAgentIcon />}
                        sx={{ borderRadius: 999, fontWeight: 900, px: 2.5 }}
                      >
                        Support kontaktieren
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ShoppingBagIcon />}
                        sx={{ borderRadius: 999, fontWeight: 900, px: 2.5 }}
                      >
                        Bestellungen ansehen
                      </Button>
                    </Stack>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                      gap: 1.25,
                    }}
                  >
                    {[
                      { icon: <ShoppingBagIcon fontSize="small" />, title: 'Bestellung & Lieferung', text: 'Status, Tracking, Probleme' },
                      { icon: <CreditCardIcon fontSize="small" />, title: 'Zahlung & Rechnung', text: 'Zahlarten, Belege, Fehler' },
                      { icon: <VerifiedUserIcon fontSize="small" />, title: 'Sicherheit', text: 'Melden, Schutz & Tipps' },
                    ].map((b, i) => (
                      <Box
                        key={i}
                        sx={(t: Theme) => ({
                          borderRadius: 3,
                          border: `1px solid ${t.palette.divider}`,
                          p: 1.5,
                          backgroundColor: t.palette.action.hover,
                        })}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={(t: Theme) => ({
                              width: 34,
                              height: 34,
                              borderRadius: 2.5,
                              display: 'grid',
                              placeItems: 'center',
                              color: t.palette.primary.main,
                              backgroundColor: t.palette.primary.main + '14',
                              border: `1px solid ${t.palette.primary.main}2A`,
                            })}
                          >
                            {b.icon}
                          </Box>
                          <Typography sx={{ fontWeight: 950 }}>{b.title}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                          {b.text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default KundencenterPage;
